export default function Pricing() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingPlans = [
    {
      id: 1,
      name: 'Monthly Membership',
      price: '$135',
      period: 'per month',
      popular: true,
      features: [
        'Unlimited class access',
        'Open gym sessions included',
        'Priority class booking',
        'Member-only events',
        '10% off merchandise',
        'Bring a friend once/month'
      ],
      cta: 'Sign Up'
    },
    {
      id: 2,
      name: 'Drop-In Pricing',
      price: '$25',
      period: 'per session',
      popular: false,
      features: [
        'Single class or open gym',
        'No commitment required',
        'Perfect for visitors',
        'Try before you commit',
        'All equipment included',
        'Valid for 1 session'
      ],
      cta: 'Book Now'
    },
    {
      id: 3,
      name: 'Open Gym Pass',
      price: '$100',
      period: 'per month',
      popular: false,
      features: [
        'Unlimited open gym access',
        'Self-directed training',
        'Flexible schedule',
        'All obstacles available',
        'Perfect for experienced ninjas',
        'Month-to-month billing'
      ],
      cta: 'Sign Up'
    },
    {
      id: 4,
      name: 'Event Packages',
      price: 'Custom',
      period: 'pricing',
      popular: false,
      features: [
        'Birthday parties',
        'Team building events',
        'Private group sessions',
        'Competition hosting',
        'Customized experiences',
        'Dedicated staff support'
      ],
      cta: 'Contact Us',
      special: true
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="mb-4">
            <span className="text-[#FF6B35] text-sm font-bold uppercase tracking-wider">
              Pricing
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-[#0A1628] mb-6">
            Flexible Options for Everyone
          </h2>
        </div>

        {/* Pricing Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl relative ${
                plan.special
                  ? 'bg-[#0A1628] text-white'
                  : 'bg-white hover:-translate-y-2'
              } ${plan.popular ? 'lg:transform lg:scale-105 shadow-xl' : ''}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#FF6B35] text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                    Most Popular
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-8">
                <h3
                  className={`text-2xl font-bold mb-4 ${
                    plan.special ? 'text-white' : 'text-[#0A1628]'
                  }`}
                >
                  {plan.name}
                </h3>
                <div
                  className={`text-5xl font-black mb-2 ${
                    plan.special ? 'text-white' : 'text-[#0A1628]'
                  }`}
                >
                  {plan.price}
                </div>
                <div
                  className={`text-sm ${
                    plan.special ? 'text-white/70' : 'text-gray-500'
                  }`}
                >
                  {plan.period}
                </div>
              </div>

              {/* Divider */}
              <div
                className={`h-px mb-8 ${
                  plan.special ? 'bg-white/20' : 'bg-gray-200'
                }`}
              ></div>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      <i
                        className={`ri-check-line text-xl ${
                          plan.special ? 'text-[#FF6B35]' : 'text-[#FF6B35]'
                        }`}
                      ></i>
                    </div>
                    <span
                      className={`ml-3 ${
                        plan.special ? 'text-white/90' : 'text-gray-600'
                      }`}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
              onClick={() => scrollToSection('contact')}
                className={`w-full py-4 rounded-full font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  plan.special
                    ? 'bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A1628]'
                    : 'bg-[#FF6B35] text-white hover:bg-[#ff5722] hover:scale-105'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12">
          <p className="text-gray-600">
            All memberships include access to our world-class facility and expert coaching.{' '}
            <span 
            onClick={() => scrollToSection('contact')}
            className="text-[#FF6B35] font-semibold cursor-pointer hover:underline">
              Contact us
            </span>{' '}
            for family and group discounts.
          </p>
        </div>
      </div>
    </section>
  );
}